import { ArrowLeft, ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BasicModal } from "@/components/common/BasicModal";
import { UserAvatarWithName } from "@/components/common/UserAvatarWithName";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { STUDYGROUP_MEMBERSHIP_STATUS_MEMBER } from "@/constants";
import { useStudyGroupMemberships } from "@/hooks/useStudyGroupMemberships";
import type { UserShort } from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";

interface AcceptParticipantsContentProps {
  isActive: boolean;
  studyGroupId: number | null;
  onClose: () => void;
  onConfirmAccept: (userIds: number[]) => Promise<void> | void;
  onConfirmDecline: (userIds: number[]) => Promise<void> | void;
  isSubmitting: boolean;
  excludedUserIds?: number[];
  attendingUsers?: UserShort[];
  initialTab?: "available" | "attending";
  onBack?: () => void;
}

export const AcceptParticipantsContent: React.FC<
  AcceptParticipantsContentProps
> = ({
  isActive,
  studyGroupId,
  onClose,
  onConfirmAccept,
  onConfirmDecline,
  isSubmitting,
  excludedUserIds = [],
  attendingUsers = [],
  initialTab = "available",
  onBack,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAvailableIds, setSelectedAvailableIds] = useState<Set<number>>(
    new Set(),
  );
  const [selectedAttendingIds, setSelectedAttendingIds] = useState<Set<number>>(
    new Set(),
  );
  const [activeTab, setActiveTab] = useState<"available" | "attending">(
    initialTab,
  );

  const {
    data: members,
    pagination,
    isLoading,
    searchMembers,
    changePage,
  } = useStudyGroupMemberships(
    studyGroupId,
    20,
    1,
    undefined,
    STUDYGROUP_MEMBERSHIP_STATUS_MEMBER,
  );

  const excludedIdsSet = useMemo(
    () => new Set(excludedUserIds),
    [excludedUserIds],
  );

  useEffect(() => {
    if (isActive) {
      setActiveTab(initialTab);
    } else {
      setSearchTerm("");
      setSelectedAvailableIds(new Set());
      setSelectedAttendingIds(new Set());
    }
  }, [isActive, initialTab]);

  useEffect(() => {
    setSelectedAvailableIds(
      (prev) => new Set([...prev].filter((id) => !excludedIdsSet.has(id))),
    );
  }, [excludedIdsSet]);

  const toggleSelection = (
    userId?: number,
    options: { scope: "available" | "attending" } = { scope: "available" },
  ) => {
    if (userId == null) return;
    const setState =
      options.scope === "available"
        ? setSelectedAvailableIds
        : setSelectedAttendingIds;
    setState((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleIds = (
    ids: Array<number | null | undefined>,
    options: { scope: "available" | "attending" } = { scope: "available" },
  ) => {
    const setState =
      options.scope === "available"
        ? setSelectedAvailableIds
        : setSelectedAttendingIds;
    setState((prev) => {
      const next = new Set(prev);
      const validIds = ids.filter((id): id is number => id != null);
      const allSelected =
        validIds.length > 0 && validIds.every((id) => next.has(id));
      if (allSelected) {
        validIds.forEach((id) => {
          next.delete(id);
        });
      } else {
        validIds.forEach((id) => {
          next.add(id);
        });
      }
      return next;
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    searchMembers(value || undefined);
  };

  const availableMembers = useMemo(
    () =>
      members.filter(
        (member) =>
          member.user.id != null && !excludedIdsSet.has(member.user.id),
      ),
    [members, excludedIdsSet],
  );

  const availableRegular = useMemo(
    () =>
      availableMembers.filter(
        (member) => !member.user.is_blocked_by_current_user,
      ),
    [availableMembers],
  );
  const availableBlocked = useMemo(
    () =>
      availableMembers.filter(
        (member) => member.user.is_blocked_by_current_user,
      ),
    [availableMembers],
  );

  const selectedMembers = useMemo(() => {
    return availableMembers
      .map((member) => member.user)
      .filter(
        (user) =>
          user.id != null && selectedAvailableIds.has(user.id as number),
      );
  }, [availableMembers, selectedAvailableIds]);

  const attendingSelectable = useMemo(
    () => (attendingUsers || []).filter((user) => user.id != null),
    [attendingUsers],
  );

  const attendingRegular = useMemo(
    () =>
      attendingSelectable.filter((user) => !user.is_blocked_by_current_user),
    [attendingSelectable],
  );
  const attendingBlocked = useMemo(
    () => attendingSelectable.filter((user) => user.is_blocked_by_current_user),
    [attendingSelectable],
  );

  const attendingIdsSet = useMemo(
    () =>
      new Set(
        attendingSelectable
          .map((user) => user.id)
          .filter((id): id is number => typeof id === "number"),
      ),
    [attendingSelectable],
  );

  const filteredSelectedAttendingIds = useMemo(
    () =>
      Array.from(selectedAttendingIds).filter((id) => attendingIdsSet.has(id)),
    [selectedAttendingIds, attendingIdsSet],
  );

  const renderUserSections = (
    regular: UserShort[],
    blocked: UserShort[],
    scope: "available" | "attending",
    emptyText: string,
  ) => {
    if (regular.length === 0 && blocked.length === 0) {
      return (
        <div className="flex items-center justify-center py-10 text-gray-500">
          {emptyText}
        </div>
      );
    }

    return (
      <div className="space-y-3 p-3">
        {regular.length > 0 && (
          <div className="divide-y divide-gray-100">
            {regular.map((user) => renderMember(user, { scope }))}
          </div>
        )}
        {blocked.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
              {t("studyGroup.blockedMembers")}
              <span className="font-medium text-gray-400">
                ({blocked.length})
              </span>
            </div>
            <div className="divide-y divide-gray-100">
              {blocked.map((user) =>
                renderMember(user, { isBlocked: true, scope }),
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleConfirm = async () => {
    try {
      if (activeTab === "attending") {
        if (filteredSelectedAttendingIds.length === 0) return;
        await onConfirmDecline(filteredSelectedAttendingIds);
      } else {
        if (selectedAvailableIds.size === 0) return;
        await onConfirmAccept(Array.from(selectedAvailableIds));
      }
      setSelectedAvailableIds(new Set());
      setSelectedAttendingIds(new Set());
      onClose();
    } catch (error) {
      console.error("Failed to accept selected participants:", error);
    }
  };

  const renderMember = (
    user: UserShort,
    options: { isBlocked?: boolean; scope?: "available" | "attending" } = {
      scope: "available",
    },
  ) => (
    <button
      key={user.id}
      type="button"
      className={cn(
        "flex w-full items-center justify-between rounded-lg border border-gray-100 px-3 py-2 text-left transition hover:border-rw-main",
        options?.isBlocked ? "opacity-70" : "",
      )}
      onClick={() =>
        toggleSelection(user.id, { scope: options.scope || "available" })
      }
    >
      <div className="flex items-center gap-3">
        <Checkbox
          checked={
            options.scope === "attending"
              ? user.id != null && selectedAttendingIds.has(user.id)
              : user.id != null && selectedAvailableIds.has(user.id)
          }
        />
        <UserAvatarWithName user={user} size="sm" showName />
      </div>
    </button>
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {onBack && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mb-3 px-0 text-gray-600 hover:bg-transparent hover:text-gray-900"
              onClick={onBack}
            >
              <ArrowLeft className="size-4" />
              {t("common.back")}
            </Button>
          )}
          <h2 className="text-lg font-semibold text-gray-900">
            {t("eventManage.attendanceManagement")}
          </h2>
          <p className="text-sm text-gray-600">
            {t("eventManage.attendanceManagementDescription")}
          </p>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as "available" | "attending")}
        className="w-full"
      >
        <TabsList className="flex w-full">
          <TabsTrigger value="attending" className="flex-1">
            {t("eventManage.tabCurrentAttendees")}
          </TabsTrigger>
          <TabsTrigger value="available" className="flex-1">
            {t("eventManage.tabAvailableAttendees")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="available" className="mt-4 space-y-3">
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2">
            <Search className="size-4 text-gray-500" />
            <Input
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t("studyGroup.searchRWUsersPlaceholder")}
              className="border-0 p-0 shadow-none focus-visible:ring-0"
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleIds(availableMembers.map((m) => m.user.id))}
              disabled={availableMembers.length === 0}
            >
              {t("common.toggleSelectAll")}
            </Button>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white">
            {isLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-500">
                {t("common.loading")}
              </div>
            ) : availableMembers.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-gray-500">
                {t("eventManage.noStudyGroupMembers")}
              </div>
            ) : (
              renderUserSections(
                availableRegular.map((member) => member.user),
                availableBlocked.map((member) => member.user),
                "available",
                t("eventManage.noStudyGroupMembers"),
              )
            )}
          </div>

          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => changePage(Math.max(1, pagination.page - 1))}
                disabled={pagination.page <= 1}
              >
                <ChevronLeft className="size-4" />
                {t("common.prev")}
              </Button>
              <span className="text-sm text-gray-600">
                {pagination.page} / {pagination.pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  changePage(Math.min(pagination.pages, pagination.page + 1))
                }
                disabled={pagination.page >= pagination.pages}
              >
                {t("common.next")}
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}

          {selectedMembers.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
              <p className="mb-2 text-sm font-medium text-gray-800">
                {t("common.selectedCount", { count: selectedMembers.length })}
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedMembers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm"
                  >
                    <UserAvatarWithName user={user} size="xs" showName />
                    <button
                      type="button"
                      className="flex size-6 items-center justify-center rounded-full bg-white text-gray-500 shadow-sm hover:bg-gray-100"
                      onClick={() =>
                        user.id ? toggleSelection(user.id) : undefined
                      }
                      aria-label={t("common.remove")}
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="attending" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleIds(attendingSelectable.map((u) => u.id))}
                disabled={attendingSelectable.length === 0}
              >
                {t("common.toggleSelectAll")}
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                size="sm"
                disabled={
                  filteredSelectedAttendingIds.length === 0 || isSubmitting
                }
                onClick={handleConfirm}
              >
                {isSubmitting
                  ? t("common.updating")
                  : t("eventManage.declineSelected")}
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white">
            {attendingSelectable.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-gray-500">
                {t("eventManage.noAttendee")}
              </div>
            ) : (
              renderUserSections(
                attendingRegular,
                attendingBlocked,
                "attending",
                t("eventManage.noAttendee"),
              )
            )}
          </div>
        </TabsContent>
      </Tabs>

      {activeTab === "available" && (
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 pt-4">
          <Button
            variant="teal"
            size="sm"
            onClick={handleConfirm}
            disabled={selectedAvailableIds.size === 0 || isSubmitting}
          >
            {isSubmitting
              ? t("common.updating")
              : t("eventManage.acceptSelected")}
          </Button>
        </div>
      )}
    </div>
  );
};

interface AcceptParticipantsDialogProps
  extends Omit<AcceptParticipantsContentProps, "isActive" | "onBack"> {
  open: boolean;
}

export const AcceptParticipantsDialog: React.FC<
  AcceptParticipantsDialogProps
> = ({ open, ...contentProps }) => {
  return (
    <BasicModal
      isOpen={open}
      onClose={contentProps.onClose}
      maxWidth="max-w-4xl"
      maxHeight="max-h-[85vh]"
    >
      <AcceptParticipantsContent isActive={open} {...contentProps} />
    </BasicModal>
  );
};
